/**
 * «منشی هوشمند» while it records (PM-0080) — the only loud state of the record
 * button: a small dark capsule with the stop button, the time and the live level,
 * and a quiet × that throws the recording away. It is gone the moment recording
 * stops, so a glance says whether the microphone is on.
 *
 * Dark on every theme and in both places it appears — the desk's sidebar row and
 * the phone's round button — so its colours are its own, not the theme's.
 */
import React from 'react';
import { X } from 'lucide-react';

import { useTranslation } from '../i18n';
import { COUNTDOWN_S } from './useSecretaryController';

export const ISLAND = '#111417';
const ISLAND_EDGE = 'rgba(255, 255, 255, 0.14)';
export const REC = '#FF453A';
export const AMBER = '#FFB340';

/** Spinner and its reduced-motion opt-out, for every surface of the button. */
export const SPIN_CSS = '@keyframes secretarySpin{to{transform:rotate(360deg)}}'
  + '.secretary-spin{animation:secretarySpin .9s linear infinite}'
  + '@media (prefers-reduced-motion: reduce){.secretary-spin{animation:none}}';

/** "m:ss", ASCII — rendered through formatDigits. */
export function shortClock(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Seven bars, each its own share of the level, so speech looks like speech.
const WEIGHTS = [0.45, 0.8, 1, 0.65, 0.9, 0.5, 0.7];

/** The live level: only this re-renders on each reading (subscribeLevel). */
function LevelMeter({ subscribe }) {
  const [level, setLevel] = React.useState(0);
  React.useEffect(() => subscribe?.(setLevel), [subscribe]);
  const scale = Math.min(1, level / 0.12);
  return (
    <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, height: 18 }}>
      {WEIGHTS.map((w, i) => (
        <i
          key={i}
          style={{
            display: 'block', width: 2.5, borderRadius: 2, background: REC,
            height: Math.max(3, Math.round(18 * Math.max(0.17, scale * w))),
            transition: 'height 90ms linear',
          }}
        />
      ))}
    </span>
  );
}

/**
 * `controls` — the record button (useSecretaryController). `collapsed`: the desk's
 * rail is folded, and only the stop button fits. `compact`: the phone, where the
 * capsule hugs its content instead of filling a row.
 */
export default function RecordCapsule({ controls: c, collapsed = false, compact = false, height = 44 }) {
  const { t, formatDigits, formatNumber } = useTranslation();
  const counting = c.live && c.remaining <= COUNTDOWN_S;
  const time = counting ? `−${shortClock(c.remaining)}` : shortClock(c.seconds);
  return (
    <div
      role="group"
      aria-label={t('secretary.recorder.recording')}
      data-testid="secretary-recording"
      data-live={c.live ? 'true' : 'false'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, height, boxSizing: 'border-box',
        paddingInline: collapsed ? 5 : '6px 8px', borderRadius: 999,
        background: ISLAND, border: `1px solid ${ISLAND_EDGE}`, color: '#fff',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.22)',
      }}
    >
      <style>{SPIN_CSS}</style>
      <button
        type="button"
        onClick={c.stop}
        aria-label={t('secretary.recorder.stop')}
        title={t('secretary.recorder.stop')}
        aria-keyshortcuts="F1"
        data-testid="secretary-stop"
        style={{
          width: 32, height: 32, padding: 0, flexShrink: 0, borderRadius: 999, cursor: 'pointer',
          border: '2px solid rgba(255, 255, 255, 0.92)', background: 'transparent',
          display: 'grid', placeItems: 'center',
        }}
      >
        <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 3, background: REC }} />
      </button>
      {collapsed ? null : (
        <>
          <b
            dir="ltr"
            data-testid="secretary-timer"
            data-counting={counting ? 'true' : 'false'}
            style={{ minWidth: '2.6em', fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: counting ? AMBER : '#fff' }}
          >
            {c.live ? formatDigits(time) : '…'}
          </b>
          <span style={{ flex: compact ? '0 0 auto' : 1, minWidth: 36, display: 'flex', justifyContent: 'center' }}>
            <LevelMeter subscribe={c.subscribeLevel} />
          </span>
          {c.pending > 0 ? (
            <span
              role="img"
              className="secretary-spin"
              aria-label={t('secretary.recorder.queued', { count: formatNumber(c.pending) })}
              title={t('secretary.recorder.queued', { count: formatNumber(c.pending) })}
              data-testid="secretary-queued"
              style={{
                width: 13, height: 13, flexShrink: 0, borderRadius: 999, boxSizing: 'border-box',
                border: '2px solid rgba(255, 255, 255, 0.25)', borderTopColor: 'rgba(255, 255, 255, 0.9)',
              }}
            />
          ) : null}
          <button
            type="button"
            onClick={c.discard}
            aria-label={t('secretary.recorder.discard')}
            title={t('secretary.recorder.discard')}
            data-testid="secretary-discard"
            style={{
              width: 24, height: 24, padding: 0, flexShrink: 0, borderRadius: 999, border: 0, cursor: 'pointer',
              background: 'rgba(255, 255, 255, 0.12)', color: 'rgba(255, 255, 255, 0.75)',
              display: 'grid', placeItems: 'center',
            }}
          >
            <X size={12} strokeWidth={2.6} aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}
